
import {
  BuildingsIcon,
} from '@phosphor-icons/react'
import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import { cn } from '@/lib/utils';

interface Organization {
  id?: string;
  name?: string;
  slug?: string;
}

interface OrgAvatarProps {
  organization: Organization | null;
  logoUrl?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  showFallbackIcon?: boolean;
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
  '2xl': 'h-32 w-32 text-xl',
};

const iconSizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-6 w-6',
  '2xl': 'h-12 w-12',
};

/**
 * OrgAvatar - A component for displaying organization avatars/logos
 * 
 * Handles organization logos with proper fallbacks to initials or icon.
 * Reuses the base Avatar component for consistent styling.
 */
export function OrgAvatar({
  organization,
  logoUrl,
  size = 'md',
  className,
  showFallbackIcon = true
}: OrgAvatarProps) {
  // Organization initials (React Compiler handles memoization)
  const initials = (() => {
    const name = organization?.name;
    if (!name) return '';

    const words = name.trim().split(' ');
    if (words.length === 1) {
      // For single word, take first two letters
      return name.substring(0, 2).toUpperCase();
    }
    // For multiple words, take first letter of each word (max 2)
    return words
      .slice(0, 2)
      .map(word => word[0])
      .join('')
      .toUpperCase();
  })();

  // If no organization provided, show icon fallback
  if (!organization || !organization.name) {
    return (
      <Avatar className={cn(sizeClasses[size], 'rounded-lg', className)}>
        <AvatarFallback className="bg-muted/20 text-muted-foreground rounded-lg">
          {showFallbackIcon ? (
            <BuildingsIcon className={iconSizeClasses[size]} />
          ) : (
            '?'
          )}
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar className={cn(sizeClasses[size], 'rounded-lg', className)}>
      {logoUrl && (
        <AvatarImage 
          src={logoUrl} 
          alt={organization.name}
          className="rounded-lg"
          loading="lazy"
        />
      )}
      <AvatarFallback 
        className="font-semibold rounded-lg"
        identifier={organization.id || organization.name}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}